/** AI B2B Leads: the editable filter form. Built from the server's vocabulary, so it can never offer a filter the
 *  server would drop. */
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableMultiSelect } from "./SearchableMultiSelect";
import type { FilterFieldSpec, FormValues } from "./types";

interface Props {
  fields: FilterFieldSpec[];
  values: FormValues;
  onChange: (next: FormValues) => void;
}

export function useFieldLabels() {
  const { t } = useTranslation();
  return {
    field: (f: FilterFieldSpec) => t(`prospectLeads.filters.field.${f.key}`, { defaultValue: f.label }),
    option: (f: FilterFieldSpec, o: string) => t(`prospectLeads.filters.option.${f.key}.${o}`, { defaultValue: o }),
  };
}

export function LeadFilterForm({ fields, values, onChange }: Props) {
  const { t } = useTranslation();
  const label = useFieldLabels();
  const set = (key: string, v: FormValues[string]) => onChange({ ...values, [key]: v });

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="lead-filter-form">
      {fields.map((f) => {
        const id = `lead-filter-${f.key}`;
        const v = values[f.key];
        if (f.kind === "single_select" && typeof v === "string") {
          return (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={id}>{label.field(f)}{f.required && <span className="text-destructive"> *</span>}</Label>
              <Select value={v} onValueChange={(x) => set(f.key, x)}>
                <SelectTrigger id={id} aria-label={label.field(f)} data-testid={id}><SelectValue placeholder={t("prospectLeads.filters.choose")} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(f.options ?? []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{f.hint}</p>
            </div>
          );
        }
        if (f.kind === "text_list" && typeof v === "string") {
          return (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={id}>{label.field(f)}</Label>
              <Input id={id} value={v} onChange={(e) => set(f.key, e.target.value)} placeholder={f.hint} maxLength={600} data-testid={id} />
              <p className="text-xs text-muted-foreground">{t("prospectLeads.filters.listHint")}</p>
            </div>
          );
        }
        if (f.kind === "multi_select" && f.searchable && Array.isArray(v)) {
          return (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={id}>{label.field(f)}</Label>
              <SearchableMultiSelect id={id} label={label.field(f)} options={f.options ?? []} value={v} onChange={(next) => set(f.key, next)} max={f.maxItems} />
              <p className="text-xs text-muted-foreground">{f.hint}</p>
            </div>
          );
        }
        if (f.kind === "multi_select" && Array.isArray(v)) {
          return (
            <div key={f.key} className="space-y-1.5 sm:col-span-2">
              <Label>{label.field(f)}</Label>
              <div className="flex flex-wrap gap-2" role="group" aria-label={label.field(f)}>
                {(f.options ?? []).map((o) => {
                  const on = v.includes(o);
                  return (
                    <Button
                      key={o}
                      type="button"
                      size="sm"
                      variant={on ? "default" : "outline"}
                      aria-pressed={on}
                      onClick={() => set(f.key, on ? v.filter((x) => x !== o) : [...v, o])}
                      data-testid={`${id}-${o}`}
                    >
                      {label.option(f, o)}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{f.hint}</p>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
